import React, { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Users, Plus, Search, Filter, Building2, Mail, Phone, FileText,
  CheckCircle, AlertTriangle, XCircle, Clock, Send, Eye, Edit,
  Trash2, Upload, ChevronRight, MessageSquare, FileCheck, X
} from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const SubcontractorsPage = () => {
  const { can } = usePermissions();
  const [subcontractors, setSubcontractors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tradeFilter, setTradeFilter] = useState("all");
  
  // Dialogs
  const [addSubDialogOpen, setAddSubDialogOpen] = useState(false);
  const [viewSubDialogOpen, setViewSubDialogOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [docTypeDialogOpen, setDocTypeDialogOpen] = useState(false);
  const [uploadDocDialogOpen, setUploadDocDialogOpen] = useState(false);

  // Form states
  const [newSub, setNewSub] = useState({
    company_name: "",
    primary_contact_name: "",
    email: "",
    phone: "",
    trades: [],
    service_areas: [],
    notes: ""
  });

  const [newDocType, setNewDocType] = useState({
    name: "",
    description: "",
    is_required: false,
    requires_expiration: true
  });

  const [uploadDoc, setUploadDoc] = useState({
    doc_type: "",
    file_url: "",
    filename: "",
    expiration_date: ""
  });

  const trades = [
    "Electrical", "Plumbing", "HVAC", "Framing", "Roofing", "Flooring",
    "Drywall", "Painting", "Concrete", "Landscaping", "Demolition",
    "Insulation", "Windows & Doors", "Cabinetry", "Tile", "General Labor"
  ];

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, typesRes] = await Promise.all([
        API.get("/subcontractors"),
        API.get("/document-types")
      ]);
      setSubcontractors(subsRes.data);
      setDocumentTypes(typesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSub = async () => {
    if (!newSub.company_name || !newSub.email) {
      toast.error("Company name and email are required");
      return;
    }
    try {
      await API.post("/subcontractors", newSub);
      toast.success("Subcontractor added");
      setAddSubDialogOpen(false);
      setNewSub({
        company_name: "",
        primary_contact_name: "",
        email: "",
        phone: "",
        trades: [],
        service_areas: [],
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add subcontractor");
    }
  };

  const handleSendInvite = async (subId) => {
    try {
      await API.post(`/subcontractors/${subId}/invite`);
      toast.success("Portal invite sent");
      fetchData();
    } catch (error) {
      toast.error("Failed to send invite");
    }
  };

  const handleViewSub = async (sub) => {
    try {
      const response = await API.get(`/subcontractors/${sub.subcontractor_id}`);
      setSelectedSub(response.data);
      setViewSubDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load subcontractor details");
    }
  };

  const handleCreateDocType = async () => {
    if (!newDocType.name) {
      toast.error("Document type name is required");
      return;
    }
    try {
      await API.post("/document-types", newDocType);
      toast.success("Document type added");
      setDocTypeDialogOpen(false);
      setNewDocType({ name: "", description: "", is_required: false, requires_expiration: true });
      fetchData();
    } catch (error) {
      toast.error("Failed to add document type");
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadDoc.doc_type || !uploadDoc.file_url) {
      toast.error("Document type and file are required");
      return;
    }
    try {
      await API.post("/subcontractor-documents", {
        subcontractor_id: selectedSub.subcontractor_id,
        ...uploadDoc
      });
      toast.success("Document uploaded");
      setUploadDocDialogOpen(false);
      setUploadDoc({ doc_type: "", file_url: "", filename: "", expiration_date: "" });
      handleViewSub(selectedSub);
    } catch (error) {
      toast.error("Failed to upload document");
    }
  };

  const handleDeleteSub = async (subId) => {
    if (!window.confirm("Are you sure you want to deactivate this subcontractor?")) return;
    try {
      await API.delete(`/subcontractors/${subId}`);
      toast.success("Subcontractor deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate subcontractor");
    }
  };

  const getComplianceBadge = (status) => {
    const badges = {
      good: { color: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30", icon: CheckCircle, label: "Good" },
      expiring: { color: "bg-amber-500/20 text-amber-400 border border-amber-500/30", icon: AlertTriangle, label: "Expiring" },
      blocked: { color: "bg-red-500/20 text-red-400 border border-red-500/30", icon: XCircle, label: "Blocked" },
      pending: { color: "bg-slate-500/20 text-slate-400 border border-slate-500/30", icon: Clock, label: "Pending" }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        <Icon size={12} />
        {badge.label}
      </Badge>
    );
  };

  const filteredSubs = subcontractors.filter(sub => {
    if (searchQuery && !sub.company_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !sub.email.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (statusFilter !== "all" && sub.compliance_status !== statusFilter) {
      return false;
    }
    if (tradeFilter !== "all" && !sub.trades?.includes(tradeFilter)) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Subcontractors</h1>
          <p className="text-slate-400 font-ibm mt-1">Manage your subcontractor network and compliance</p>
        </div>
        <div className="flex gap-2">
          {can('manage_document_types') && (
            <Button variant="outline" onClick={() => setDocTypeDialogOpen(true)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="manage-doc-types-btn">
              <FileText size={16} className="mr-2" />
              Document Types
            </Button>
          )}
          {can('add_subcontractor') && (
            <Button onClick={() => setAddSubDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="add-subcontractor-btn">
              <Plus size={16} className="mr-2" />
              Add Subcontractor
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Subs</p>
                <p className="text-2xl font-bold text-white">{subcontractors.length}</p>
              </div>
              <Users className="h-8 w-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Compliant</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {subcontractors.filter(s => s.compliance_status === "good").length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Expiring Soon</p>
                <p className="text-2xl font-bold text-amber-400">
                  {subcontractors.filter(s => s.compliance_status === "expiring").length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Blocked</p>
                <p className="text-2xl font-bold text-red-400">
                  {subcontractors.filter(s => s.compliance_status === "blocked").length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            placeholder="Search by company or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-700 text-white placeholder-slate-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-300">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-200">All Status</SelectItem>
            <SelectItem value="good" className="text-slate-200">Compliant</SelectItem>
            <SelectItem value="expiring" className="text-slate-200">Expiring</SelectItem>
            <SelectItem value="blocked" className="text-slate-200">Blocked</SelectItem>
            <SelectItem value="pending" className="text-slate-200">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tradeFilter} onValueChange={setTradeFilter}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-300">
            <SelectValue placeholder="Trade" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-200">All Trades</SelectItem>
            {trades.map(t => (
              <SelectItem key={t} value={t} className="text-slate-200">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Subcontractor List */}
      <div className="space-y-3">
        {filteredSubs.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No subcontractors found</p>
              <Button className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-slate-900" onClick={() => setAddSubDialogOpen(true)}>
                <Plus size={16} className="mr-2" />
                Add Your First Subcontractor
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredSubs.map((sub) => (
            <Card key={sub.subcontractor_id} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer" onClick={() => handleViewSub(sub)}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-slate-800 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-slate-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{sub.company_name}</h3>
                      <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {sub.email}
                        </span>
                        {sub.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {sub.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {sub.trades?.slice(0, 3).map(t => (
                        <Badge key={t} variant="outline" className="text-xs border-slate-700 text-slate-300">{t}</Badge>
                      ))}
                      {sub.trades?.length > 3 && (
                        <Badge variant="outline" className="text-xs border-slate-700 text-slate-300">+{sub.trades.length - 3}</Badge>
                      )}
                    </div>
                    {getComplianceBadge(sub.compliance_status)}
                    <ChevronRight className="text-slate-500" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Subcontractor Dialog */}
      <Dialog open={addSubDialogOpen} onOpenChange={setAddSubDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Subcontractor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Company Name *</label>
              <Input
                value={newSub.company_name}
                onChange={(e) => setNewSub({ ...newSub, company_name: e.target.value })}
                placeholder="ABC Electric Co."
                className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-400 mb-1 block">Contact Name</label>
                <Input
                  value={newSub.primary_contact_name}
                  onChange={(e) => setNewSub({ ...newSub, primary_contact_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={newSub.email}
                  onChange={(e) => setNewSub({ ...newSub, email: e.target.value })}
                  placeholder="john@abcelectric.com"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Phone</label>
              <Input
                value={newSub.phone}
                onChange={(e) => setNewSub({ ...newSub, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Trades</label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-slate-700 rounded-md bg-slate-800">
                {trades.map(t => (
                  <Badge
                    key={t}
                    variant={newSub.trades.includes(t) ? "default" : "outline"}
                    className={`cursor-pointer ${newSub.trades.includes(t) ? 'bg-emerald-500 text-white' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}
                    onClick={() => {
                      if (newSub.trades.includes(t)) {
                        setNewSub({ ...newSub, trades: newSub.trades.filter(x => x !== t) });
                      } else {
                        setNewSub({ ...newSub, trades: [...newSub.trades, t] });
                      }
                    }}
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Notes</label>
              <Textarea
                value={newSub.notes}
                onChange={(e) => setNewSub({ ...newSub, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
                className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAddSubDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleCreateSub} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Add Subcontractor</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Subcontractor Dialog */}
      <Dialog open={viewSubDialogOpen} onOpenChange={setViewSubDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-900 border-slate-800">
          {selectedSub && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl text-white">{selectedSub.company_name}</DialogTitle>
                    <p className="text-sm text-slate-400 mt-1">{selectedSub.primary_contact_name}</p>
                  </div>
                  {getComplianceBadge(selectedSub.compliance_status)}
                </div>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail size={16} className="text-slate-500" />
                    <a href={`mailto:${selectedSub.email}`} className="text-emerald-400 hover:underline">{selectedSub.email}</a>
                  </div>
                  {selectedSub.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <Phone size={16} className="text-slate-500" />
                      <span>{selectedSub.phone}</span>
                    </div>
                  )}
                </div>

                {/* Trades */}
                {selectedSub.trades?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-2">Trades</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedSub.trades.map(t => (
                        <Badge key={t} variant="outline" className="border-slate-700 text-slate-300">{t}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-slate-400">Compliance Documents</h4>
                    <Button size="sm" variant="outline" onClick={() => setUploadDocDialogOpen(true)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                      <Upload size={14} className="mr-1" />
                      Upload Document
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {selectedSub.documents?.length > 0 ? (
                      selectedSub.documents.map(doc => (
                        <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-700">
                          <div className="flex items-center gap-3">
                            <FileCheck className="text-slate-500" size={20} />
                            <div>
                              <p className="font-medium text-white">{doc.doc_type}</p>
                              <p className="text-xs text-slate-500">
                                {doc.expiration_date ? `Expires: ${doc.expiration_date}` : "No expiration"}
                              </p>
                            </div>
                          </div>
                          <Badge className={
                            doc.status === "valid" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
                            doc.status === "expired" ? "bg-red-500/20 text-red-400 border border-red-500/30" :
                            "bg-slate-500/20 text-slate-400 border border-slate-500/30"
                          }>
                            {doc.status}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No documents uploaded yet</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-slate-800">
                  {!selectedSub.invite_token ? (
                    <Button onClick={() => handleSendInvite(selectedSub.subcontractor_id)} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                      <Send size={16} className="mr-2" />
                      Send Portal Invite
                    </Button>
                  ) : (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      Invite Sent
                    </Badge>
                  )}
                  {can('delete_subcontractor') && (
                    <Button variant="outline" onClick={() => handleDeleteSub(selectedSub.subcontractor_id)} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="deactivate-sub-btn">
                      <Trash2 size={16} className="mr-2" />
                      Deactivate
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Types Dialog */}
      <Dialog open={docTypeDialogOpen} onOpenChange={setDocTypeDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Document Types</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {documentTypes.map(dt => (
                <div key={dt.doc_type_id} className="flex items-center justify-between p-2 bg-slate-800 rounded border border-slate-700">
                  <div>
                    <p className="font-medium text-white">{dt.name}</p>
                    <p className="text-xs text-slate-500">{dt.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {dt.is_required && <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">Required</Badge>}
                    {dt.is_system && <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">System</Badge>}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-800 pt-4">
              <h4 className="text-sm font-medium text-slate-400 mb-2">Add New Type</h4>
              <div className="space-y-2">
                <Input
                  placeholder="Document type name"
                  value={newDocType.name}
                  onChange={(e) => setNewDocType({ ...newDocType, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
                <Input
                  placeholder="Description"
                  value={newDocType.description}
                  onChange={(e) => setNewDocType({ ...newDocType, description: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={newDocType.is_required}
                      onChange={(e) => setNewDocType({ ...newDocType, is_required: e.target.checked })}
                      className="rounded border-slate-600"
                    />
                    Required
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={newDocType.requires_expiration}
                      onChange={(e) => setNewDocType({ ...newDocType, requires_expiration: e.target.checked })}
                      className="rounded border-slate-600"
                    />
                    Has Expiration
                  </label>
                </div>
                <Button onClick={handleCreateDocType} className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Add Document Type</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDocDialogOpen} onOpenChange={setUploadDocDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Document Type</label>
              <Select value={uploadDoc.doc_type} onValueChange={(v) => setUploadDoc({ ...uploadDoc, doc_type: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {documentTypes.map(dt => (
                    <SelectItem key={dt.doc_type_id} value={dt.name} className="text-slate-200">{dt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">File URL</label>
              <Input
                value={uploadDoc.file_url}
                onChange={(e) => setUploadDoc({ ...uploadDoc, file_url: e.target.value })}
                placeholder="https://example.com/document.pdf"
                className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
              />
              <p className="text-xs text-slate-500 mt-1">Enter the URL of the uploaded document</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Filename</label>
              <Input
                value={uploadDoc.filename}
                onChange={(e) => setUploadDoc({ ...uploadDoc, filename: e.target.value })}
                placeholder="w9_form.pdf"
                className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-400 mb-1 block">Expiration Date</label>
              <Input
                type="date"
                value={uploadDoc.expiration_date}
                onChange={(e) => setUploadDoc({ ...uploadDoc, expiration_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDocDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancel</Button>
              <Button onClick={handleUploadDocument} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">Upload</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubcontractorsPage;
